import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';
import {
  ArrowLeft, Buildings, Lightning, Flame, FileText, EnvelopeSimple,
  Receipt, Tag, Paperclip, ChatTeardropText, ActivityIcon as Activity, Wallet,
  IdentificationCard, MapPin, Pencil, ArrowsClockwise, FloppyDisk, X,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CustomerDetailPage() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/customers/${customerId}`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
      else if (res.status === 409) {
        const d = await res.json();
        setError(d.detail || 'Lead non ancora attivo');
      } else {
        toast.error('Cliente non trovato');
        navigate('/customers');
      }
    } catch (e) { toast.error('Errore caricamento'); }
    finally { setLoading(false); }
  }, [customerId, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const startEdit = () => {
    setForm({
      ragione_sociale: data.ragione_sociale || '',
      partita_iva: data.partita_iva || '',
      codice_fiscale: data.codice_fiscale || '',
      pec: data.pec || '',
      email: data.email || '',
      telefono: data.telefono || '',
      iban: data.iban || '',
      indirizzo_sede: data.indirizzo_sede || '',
      rappresentante_legale: data.rappresentante_legale || '',
      rappresentante_cf: data.rappresentante_cf || '',
      rappresentante_telefono: data.rappresentante_telefono || '',
      consenso_marketing: !!data.consenso_marketing,
      consenso_terzi: !!data.consenso_terzi,
    });
    setEditing(true);
  };

  const cancelEdit = () => { setEditing(false); setForm({}); };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/customers/${customerId}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        toast.success('Anagrafica aggiornata');
        setEditing(false);
        await fetchData();
      } else {
        const txt = await res.text();
        toast.error(`Errore salvataggio: ${txt.slice(0, 120)}`);
      }
    } catch (e) { toast.error('Errore salvataggio'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 text-slate-500">Caricamento scheda cliente...</div>;
  if (error) return (
    <div className="p-8 max-w-2xl mx-auto">
      <Button variant="ghost" onClick={() => navigate('/leads')}>
        <ArrowLeft size={18} className="mr-1" /> Torna ai lead
      </Button>
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mt-4">
        <h2 className="font-semibold text-yellow-900">Scheda non disponibile</h2>
        <p className="text-yellow-700 mt-2">{error}</p>
        <Button onClick={() => navigate(`/leads/${customerId}`)} className="mt-4 bg-orange-500 hover:bg-orange-600">
          Apri scheda lead semplice →
        </Button>
      </div>
    </div>
  );
  if (!data) return null;

  const pods = data.pods || [];
  const podsLuce = pods.filter(p => p.pod_code);
  const podsGas = pods.filter(p => p.pdr_code);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="customer-detail-page">
      {/* HEADER */}
      <div className="flex items-center gap-3 mb-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/customers')}>
          <ArrowLeft size={18} className="mr-1" /> Clienti
        </Button>
        <div className="text-slate-300">/</div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2 flex-1">
          <Buildings size={24} weight="duotone" className="text-orange-500" />
          {data.ragione_sociale}
          <Badge className="bg-green-100 text-green-700">Cliente Attivo</Badge>
        </h1>
        <Button size="sm" variant="outline" onClick={() => navigate(`/leads/${customerId}`)}
                title="Vista lead semplice">
          <Pencil size={14} className="mr-1" /> Vista lead
        </Button>
        {!editing ? (
          <Button size="sm" onClick={startEdit} className="bg-orange-500 hover:bg-orange-600"
                  data-testid="customer-edit-btn">
            <Pencil size={14} className="mr-1" /> Modifica anagrafica
          </Button>
        ) : (
          <>
            <Button size="sm" variant="outline" onClick={cancelEdit} disabled={saving}
                    data-testid="customer-cancel-btn">
              <X size={14} className="mr-1" /> Annulla
            </Button>
            <Button size="sm" onClick={saveEdit} disabled={saving} className="bg-green-600 hover:bg-green-700"
                    data-testid="customer-save-btn">
              <FloppyDisk size={14} className="mr-1" /> {saving ? 'Salvataggio…' : 'Salva'}
            </Button>
          </>
        )}
      </div>

      {/* KPI ROW */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <KPI icon={Lightning} label="POD luce" value={podsLuce.length} color="text-yellow-600 bg-yellow-50" />
        <KPI icon={Flame} label="PDR gas" value={podsGas.length} color="text-blue-600 bg-blue-50" />
        <KPI icon={FileText} label="Contratti" value={(data.contracts || []).length} color="text-orange-600 bg-orange-50" />
        <KPI icon={Receipt} label="Fatture analizzate" value={(data.fatture || []).length} color="text-purple-600 bg-purple-50" />
      </div>

      {/* ACCORDION */}
      <Accordion type="multiple" value={editing ? ['anag', 'rappr', 'consensi', 'forn-power', 'contratti'] : ['anag', 'forn-power', 'contratti']} className="space-y-2">
        <Section value="anag" title="Anagrafica & Contatti" icon={IdentificationCard}>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <EditField label="Ragione sociale" value={data.ragione_sociale} editing={editing}
                       v={form.ragione_sociale} onChange={(v) => setForm({ ...form, ragione_sociale: v })} testid="cf-ragione" />
            <EditField label="P.IVA" value={data.partita_iva} editing={editing} mono
                       v={form.partita_iva} onChange={(v) => setForm({ ...form, partita_iva: v })} testid="cf-piva" />
            <EditField label="Codice fiscale" value={data.codice_fiscale} editing={editing} mono
                       v={form.codice_fiscale} onChange={(v) => setForm({ ...form, codice_fiscale: v })} testid="cf-cf" />
            <EditField label="PEC" value={data.pec} editing={editing}
                       v={form.pec} onChange={(v) => setForm({ ...form, pec: v })} testid="cf-pec" />
            <EditField label="Email" value={data.email} editing={editing}
                       v={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="cf-email" />
            <EditField label="Telefono" value={data.telefono} editing={editing}
                       v={form.telefono} onChange={(v) => setForm({ ...form, telefono: v })} testid="cf-telefono" />
            <EditField label="IBAN" value={data.iban} editing={editing} mono
                       v={form.iban} onChange={(v) => setForm({ ...form, iban: v.toUpperCase() })} testid="cf-iban" />
            <EditField label="Indirizzo sede" value={data.indirizzo_sede} editing={editing}
                       v={form.indirizzo_sede} onChange={(v) => setForm({ ...form, indirizzo_sede: v })} testid="cf-sede" />
          </div>
        </Section>

        <Section value="rappr" title="Rappresentante Legale" icon={IdentificationCard}>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <EditField label="Nome" value={data.rappresentante_legale} editing={editing}
                       v={form.rappresentante_legale} onChange={(v) => setForm({ ...form, rappresentante_legale: v })} testid="cf-rapp-nome" />
            <EditField label="Codice Fiscale" value={data.rappresentante_cf} editing={editing} mono
                       v={form.rappresentante_cf} onChange={(v) => setForm({ ...form, rappresentante_cf: v })} testid="cf-rapp-cf" />
            <EditField label="Telefono" value={data.rappresentante_telefono} editing={editing}
                       v={form.rappresentante_telefono} onChange={(v) => setForm({ ...form, rappresentante_telefono: v })} testid="cf-rapp-tel" />
          </div>
        </Section>

        <Section value="consensi" title="Consensi Privacy" icon={IdentificationCard}>
          <div className="grid grid-cols-2 gap-3 text-sm">
            {editing ? (
              <>
                <label className="flex items-center gap-2 cursor-pointer p-2 border rounded bg-slate-50">
                  <Checkbox checked={!!form.consenso_marketing}
                            onCheckedChange={(v) => setForm({ ...form, consenso_marketing: !!v })}
                            data-testid="cf-cons-marketing" />
                  <span>Consenso marketing</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer p-2 border rounded bg-slate-50">
                  <Checkbox checked={!!form.consenso_terzi}
                            onCheckedChange={(v) => setForm({ ...form, consenso_terzi: !!v })}
                            data-testid="cf-cons-terzi" />
                  <span>Consenso comunicazione a terzi</span>
                </label>
              </>
            ) : (
              <>
                <Field label="Consenso marketing" value={data.consenso_marketing ? '✓ Sì' : '— No'} />
                <Field label="Consenso comunicazione a terzi" value={data.consenso_terzi ? '✓ Sì' : '— No'} />
              </>
            )}
          </div>
        </Section>

        <Section value="forn-power" title={`Fornitura POWER (${podsLuce.length} POD)`} icon={Lightning}>
          {podsLuce.length === 0 ? (
            <p className="text-sm text-slate-400">Nessuna fornitura luce</p>
          ) : (
            <div className="space-y-2">
              {podsLuce.map((p, i) => (
                <PodCard key={i} pod={p} customerId={customerId} kind="luce" navigate={navigate} />
              ))}
            </div>
          )}
        </Section>

        {podsGas.length > 0 && (
          <Section value="forn-gas" title={`Fornitura GAS (${podsGas.length} PDR)`} icon={Flame}>
            <div className="space-y-2">
              {podsGas.map((p, i) => (
                <PodCard key={i} pod={p} customerId={customerId} kind="gas" navigate={navigate} />
              ))}
            </div>
          </Section>
        )}

        <Section value="contratti" title={`Contratti (${(data.contracts || []).length})`} icon={FileText}>
          {(data.contracts || []).length === 0 ? (
            <p className="text-sm text-slate-400">Nessun contratto</p>
          ) : (
            <div className="space-y-2">
              {data.contracts.map(c => {
                const boState = c.bo_state || 'validato';
                const boColors = {
                  validato: 'bg-cyan-100 text-cyan-700',
                  da_attivare: 'bg-yellow-100 text-yellow-700',
                  in_attivazione: 'bg-blue-100 text-blue-700',
                  attivato: 'bg-green-100 text-green-700',
                  respinto_bo: 'bg-red-100 text-red-700',
                };
                return (
                  <div key={c.id} className="border rounded p-3 flex items-center justify-between hover:bg-orange-50/30">
                    <div className="flex-1">
                      <div className="font-medium text-sm">{c.nome_offerta || c.offer_name || 'Contratto'}</div>
                      <div className="text-xs text-slate-500">
                        Firmato: {(c.signed_at || c.created_at || '').slice(0, 10)}
                        {c.attivato_at && ` · Attivato: ${c.attivato_at.slice(0, 10)}`}
                        {c.numero_pratica_switch && ` · Pratica: ${c.numero_pratica_switch}`}
                      </div>
                      {c.respinto_motivo && (
                        <div className="text-xs text-red-600 mt-1 italic">⚠ {c.respinto_motivo}</div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge className={boColors[boState]}>{boState.replace('_', ' ')}</Badge>
                      <a href={`/contracts`} className="text-[10px] text-orange-600 hover:underline">
                        Apri in Back Office →
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Section>

        <Section value="fatture" title={`Fatture analizzate (${(data.fatture || []).length})`} icon={Receipt}>
          {(data.fatture || []).length === 0 ? (
            <p className="text-sm text-slate-400">Nessuna fattura analizzata</p>
          ) : (
            <div className="space-y-1">
              {data.fatture.map(f => (
                <div key={f.id} className="flex items-center justify-between text-sm border rounded p-2 hover:bg-slate-50">
                  <div>
                    <span className="font-medium">{f.fornitore_attuale || 'Fornitura'}</span>
                    <span className="text-slate-500 ml-2 font-mono text-xs">{f.pod}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-slate-500 text-xs">{f.data}</span>
                    {f.totale_eur && <span className="font-mono text-xs">{f.totale_eur} €</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section value="comunicazioni" title={`Comunicazioni (${(data.comunicazioni || []).length})`} icon={EnvelopeSimple}>
          {(data.comunicazioni || []).length === 0 ? (
            <p className="text-sm text-slate-400">Nessuna comunicazione</p>
          ) : (
            <div className="space-y-1 max-h-80 overflow-y-auto">
              {data.comunicazioni.map(cm => (
                <div key={cm.id} className="flex items-center justify-between text-xs border rounded p-2">
                  <div className="flex-1">
                    <span className="font-medium">{cm.subject || cm.type}</span>
                    <Badge variant="outline" className="ml-2 text-[10px]">{cm.type}</Badge>
                  </div>
                  <div className="flex items-center gap-2 text-slate-500">
                    {cm.opened_at && <Badge className="bg-green-100 text-green-700 text-[10px]">Aperta</Badge>}
                    <span>{(cm.created_at || '').slice(0, 10)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section value="allegati" title={`Allegati (${(data.allegati || []).length})`} icon={Paperclip}>
          {(data.allegati || []).length === 0 ? (
            <p className="text-sm text-slate-400">Nessun allegato firmato</p>
          ) : (
            <div className="space-y-1">
              {data.allegati.map(a => (
                <div key={a.id} className="flex items-center justify-between text-sm border rounded p-2">
                  <div className="flex items-center gap-2">
                    <Paperclip size={14} className="text-slate-400" />
                    <span>{a.kind}</span>
                    <span className="text-slate-400 text-xs">{a.filename}</span>
                  </div>
                  <span className="text-xs text-slate-400">{(a.signed_at || '').slice(0, 10)}</span>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section value="saldo" title="Saldo & Fatturazione (placeholder)" icon={Wallet}>
          <p className="text-sm text-slate-400 italic">
            Saldo aperto/scaduto e bonifici verranno implementati in una fase successiva (richiede integrazione con sistema fatturazione).
          </p>
        </Section>

        <Section value="tickets" title="Tickets (placeholder)" icon={ChatTeardropText}>
          <p className="text-sm text-slate-400 italic">Modulo ticketing in sviluppo.</p>
        </Section>

        <Section value="tag" title={`Tag & Note (${(data.tags || []).length})`} icon={Tag}>
          <div className="flex gap-2 flex-wrap mb-2">
            {(data.tags || []).map(t => <Badge key={t} variant="outline">{t}</Badge>)}
            {(data.tags || []).length === 0 && <span className="text-sm text-slate-400">Nessun tag</span>}
          </div>
          {data.note && <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{data.note}</p>}
        </Section>

        <Section value="attivita" title="Attività & Log" icon={Activity}>
          <p className="text-xs text-slate-500">
            Ultimo aggiornamento: {(data.updated_at || '').slice(0, 19).replace('T', ' ')}
            {data.updated_by && ` da ${data.updated_by}`}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Cliente attivato il {(data.activated_at || '').slice(0, 10)}
          </p>
        </Section>
      </Accordion>
    </div>
  );
}

function Section({ value, title, icon: Icon, children }) {
  return (
    <AccordionItem value={value} className="border rounded-lg bg-white px-4">
      <AccordionTrigger className="hover:no-underline" data-testid={`section-${value}`}>
        <div className="flex items-center gap-2">
          {Icon && <Icon size={18} weight="duotone" className="text-orange-500" />}
          <span className="font-semibold text-slate-900">{title}</span>
        </div>
      </AccordionTrigger>
      <AccordionContent className="pt-2 pb-4">{children}</AccordionContent>
    </AccordionItem>
  );
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`text-sm ${mono ? 'font-mono' : ''} ${value ? 'text-slate-900' : 'text-slate-400 italic'}`}>
        {value || '—'}
      </div>
    </div>
  );
}

function EditField({ label, value, editing, v, onChange, mono, testid }) {
  if (!editing) return <Field label={label} value={value} mono={mono} />;
  return (
    <div>
      <div className="text-xs text-slate-500 mb-1">{label}</div>
      <Input value={v || ''} onChange={(e) => onChange(e.target.value)}
             className={`h-9 ${mono ? 'font-mono text-sm' : 'text-sm'}`}
             data-testid={testid} />
    </div>
  );
}

function KPI({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white border rounded-lg p-3">
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded flex items-center justify-center ${color}`}>
          <Icon size={16} weight="duotone" />
        </div>
        <div>
          <div className="text-xs text-slate-500">{label}</div>
          <div className="text-xl font-bold">{value}</div>
        </div>
      </div>
    </div>
  );
}

function PodCard({ pod, customerId, kind, navigate }) {
  const Icon = kind === 'luce' ? Lightning : Flame;
  const codeKey = kind === 'luce' ? 'pod_code' : 'pdr_code';
  return (
    <div className={`border rounded-lg p-3 hover:shadow-sm hover:border-orange-200 transition cursor-pointer ${
                     kind === 'luce' ? 'bg-yellow-50/30' : 'bg-blue-50/30'}`}
         onClick={() => navigate(`/pods/${customerId}/${pod.index}`)}
         data-testid={`pod-card-${pod.index}`}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded flex items-center justify-center ${
                          kind === 'luce' ? 'bg-yellow-100 text-yellow-600' : 'bg-blue-100 text-blue-600'}`}>
          <Icon size={20} weight="duotone" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono font-semibold">{pod[codeKey] || '—'}</span>
            {pod.fornitore_attuale && (
              <Badge variant="outline" className="text-xs">{pod.fornitore_attuale}</Badge>
            )}
            {pod.tensione && <Badge variant="outline" className="text-xs">{pod.tensione}</Badge>}
            {pod.tipologia_uso && <Badge variant="outline" className="text-xs">{pod.tipologia_uso.replace('_', ' ')}</Badge>}
          </div>
          <div className="text-xs text-slate-600 mt-1 flex items-center gap-1">
            <MapPin size={12} className="text-slate-400" />
            {pod.indirizzo_fornitura || '—'}
            {pod.comune_fornitura && `, ${pod.comune_fornitura}`}
          </div>
          <div className="flex gap-3 text-xs text-slate-500 mt-1">
            {pod.potenza_impegnata && <span>{pod.potenza_impegnata} kW</span>}
            {pod.consumo_annuo && <span>{Number(pod.consumo_annuo).toLocaleString('it-IT')} kWh/anno</span>}
            {pod.distributore && <span>Distr: {pod.distributore}</span>}
          </div>
        </div>
        <ArrowsClockwise size={16} className="text-slate-300" />
      </div>
    </div>
  );
}
